import type { JsonSchema, UISchemaElement } from '@jsonforms/core';

/**
 * Apply field restrictions to JSON Schema and UISchema.
 *
 * - HIDDEN: Remove field from schema properties, required array, and all UISchema controls
 * - READ_ONLY: Set readOnly on the schema property and readonly option on UISchema controls
 *
 * Returns new schema/uiSchema objects (does not mutate inputs).
 */
export function applyFieldRestrictions(
  schema: JsonSchema,
  uiSchema: UISchemaElement,
  restrictions: Map<string, 'HIDDEN' | 'READ_ONLY'>
): { schema: JsonSchema; uiSchema: UISchemaElement } {
  // Defensive: if schema or uiSchema is falsy, return as-is
  if (!schema || !uiSchema || restrictions.size === 0) {
    return { schema, uiSchema };
  }

  // Deep clone to avoid mutating originals
  const newSchema = JSON.parse(JSON.stringify(schema)) as JsonSchema;
  const newUiSchema = JSON.parse(JSON.stringify(uiSchema));

  for (const [fieldName, restrictionType] of restrictions) {
    if (restrictionType === 'HIDDEN') {
      // Remove from schema properties
      if (newSchema.properties) {
        delete newSchema.properties[fieldName];
      }

      // Remove from required array
      if (Array.isArray(newSchema.required)) {
        newSchema.required = newSchema.required.filter((r: string) => r !== fieldName);
      }

      // Remove matching controls from UISchema tree
      removeControlFromUiSchema(newUiSchema, fieldName);
    } else if (restrictionType === 'READ_ONLY') {
      // Set readOnly on schema property
      if (newSchema.properties?.[fieldName]) {
        (newSchema.properties[fieldName] as any).readOnly = true;
      }

      // Set readonly option on matching UISchema controls
      setReadonlyOnControl(newUiSchema, fieldName);
    }
  }

  return { schema: newSchema, uiSchema: newUiSchema };
}

/**
 * Recursively remove any Control element whose scope matches #/properties/{fieldName}.
 * Handles nested layouts: VerticalLayout, HorizontalLayout, Group, etc.
 */
function removeControlFromUiSchema(element: any, fieldName: string): void {
  if (!element.elements || !Array.isArray(element.elements)) {
    return;
  }

  const scope = `#/properties/${fieldName}`;

  element.elements = element.elements.filter((el: any) => {
    if (el.type === 'Control' && el.scope === scope) {
      return false; // Remove this control
    }
    return true;
  });

  // Recurse into remaining elements
  for (const el of element.elements) {
    removeControlFromUiSchema(el, fieldName);
  }
}

/**
 * Recursively find Control elements matching the field and set readonly option.
 */
function setReadonlyOnControl(element: any, fieldName: string): void {
  if (!element.elements || !Array.isArray(element.elements)) {
    return;
  }

  const scope = `#/properties/${fieldName}`;

  for (const el of element.elements) {
    if (el.type === 'Control' && el.scope === scope) {
      if (!el.options) {
        el.options = {};
      }
      el.options.readonly = true;
    }
    // Recurse into nested elements
    setReadonlyOnControl(el, fieldName);
  }
}
